import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Flex } from 'antd';
import { Text } from '../Ant';
import { ITimer } from '../../interface/timer';
import { computeInitialDuration, formatTime } from '../../constants/utilityFunctions';
import {
  RunningIndicator,
  RunningTimerRow,
} from '../../assets/styles/components/organisationSwitch.styled';

const START_TIME_FORMAT = 'HH:mm';
const PRINCIPAL_SEPARATOR = ' — ';

const useElapsedSeconds = (timerId: string) => {
  const [seconds, setSeconds] = useState(() => computeInitialDuration(timerId));

  useEffect(() => {
    setSeconds(computeInitialDuration(timerId));
    const interval = setInterval(() => setSeconds(computeInitialDuration(timerId)), 1000);
    return () => clearInterval(interval);
  }, [timerId]);

  return seconds;
};

const resolvePrincipalName = (timer: ITimer): string | undefined =>
  timer.matter?.principalCompany?.name ?? timer.matter?.principalPrivatePerson?.fullName;

const buildTimerLabel = (timer: ITimer): string => {
  const principalName = resolvePrincipalName(timer);
  return principalName ? `${timer.subject}${PRINCIPAL_SEPARATOR}${principalName}` : timer.subject;
};

const RunningTimerSummary = ({ timer }: { timer: ITimer }) => {
  const { t } = useTranslation();
  const seconds = useElapsedSeconds(timer.id);

  return (
    <RunningTimerRow
      data-testid={`running-timer-summary-${timer.id}`}
      align="center"
      gap={5}
      className="w-full"
    >
      <RunningIndicator data-testid="running-timer-indicator" />
      <Flex vertical gap={2} flex="1 1 auto" className="min-w-0">
        <Text
          data-testid="running-timer-subject"
          $fontsize="13px"
          $weight={500}
          $color="title"
          $lineheight="18px"
          className="timer-text"
        >
          {buildTimerLabel(timer)}
        </Text>
        <Text
          data-testid="running-timer-started-at"
          $fontsize="12px"
          $color="description"
          $lineheight="18px"
          className="timer-text"
        >
          {t('common:organizationSwitch.runningSince', {
            time: timer?.intervalStart ? dayjs(timer.intervalStart).format(START_TIME_FORMAT) : '-',
          })}
        </Text>
      </Flex>
      <Text
        data-testid="running-timer-elapsed"
        $fontsize="14px"
        $weight={600}
        $color="primary"
        $width="auto"
        className="timer-elapsed"
      >
        {formatTime(seconds)}
      </Text>
    </RunningTimerRow>
  );
};

export default RunningTimerSummary;
